// 用户 Task 详情与启动流 / User Task detail and start flow.
// 提供任务编辑、保存、Markdown 上传、历史展示、报告选择和任务启动。
// Provides task editing, save, Markdown upload, history display, report selection, and task start.

import {
  ActiveReportGenerationError,
  ActiveSimulationError,
  InsufficientCreditError,
  RateLimitedError,
} from "@/api/client";
import { tasksApi as defaultTasksApi } from "@/api/tasks";
import type {
  TaskCreateRequest,
  TaskDeleteResponse,
  TaskDetailResponse,
  TaskListResponse,
  TaskReportsResponse,
  TaskSimulationStartResponse,
} from "@/api/tasks";
import { simulationsApi } from "@/api/simulations";
import { startButtonLoading } from "@/features/async-button";
import { renderCardList } from "@/features/card-list";
import { showConfirmSimulationModal } from "@/features/confirm-simulation-modal";
import { showInsufficientCreditModal } from "@/features/credit-insufficient-modal";
import { showInfoModal } from "@/features/info-modal";
import { showStopSimulationModal } from "@/features/stop-simulation-modal";
import {
  closeDetailModal,
  isDetailModalOpen,
  openDetailModal,
  replaceDetailModalBody,
  replaceDetailModalFooter,
  setDetailDirty,
  setDetailModalTitleMeta,
} from "@/features/detail-modal";
import { appendLog, connectEvents as defaultConnectEvents } from "@/features/log-stream";
import { currentBinding, onSimulationStarted } from "@/features/log-terminal/simulation-binder";
import { setActiveTab } from "@/features/log-terminal/terminal";
import { abTemplates, downloadTemplate, singleTemplate } from "@/features/plan-templates";
import { renderPlanMarkdownPreviewHtml } from "@/features/plan-panel";
import { notifyReportUpdated } from "@/features/report-broadcast";
import { openReportForSelection } from "@/features/report-picker";
import { escapeHtml } from "@/lib/dom";
import {
  formatConfigLabel,
  getConfigOptions,
  type ConfigKind,
  type ConfigOption,
} from "@/lib/config-labels";
import {
  defaultPlatformForChannel,
  defaultVerticalForChannel,
  isPlatformCompatible,
  isVerticalCompatible,
  platformOptionsForChannel,
  verticalOptionsForChannel,
} from "@/lib/config-coupling";
import { createLoadingBlock } from "@/lib/spinner";
import type {
  Mode,
  ReportStatus,
  SimulationConfig,
  SimulationStatus,
  TaskDetail,
  TaskHistoryItem,
  TaskListItem,
  TaskReportSummary,
} from "@/lib/types";
import { state } from "@/state/store";

interface TaskApiShape {
  create?: (body: TaskCreateRequest) => Promise<TaskDetailResponse>;
  list?: (mode?: Mode) => Promise<TaskListResponse>;
  get?: (id: string) => Promise<TaskDetailResponse>;
  update?: (id: string, body: {
    title?: string | null;
    plan_a_md?: string | null;
    plan_b_md?: string | null;
    config?: SimulationConfig | null;
  }) => Promise<TaskDetailResponse>;
  delete?: (id: string) => Promise<TaskDeleteResponse>;
  startSimulation?: (id: string) => Promise<TaskSimulationStartResponse>;
  reports?: (id: string) => Promise<TaskReportsResponse>;
}

type ConnectEvents = typeof defaultConnectEvents;
type TerminalPayload = Parameters<NonNullable<Parameters<ConnectEvents>[1]>>[0];

// Task 详情依赖注入 / Task detail dependency injection.
export interface TaskDetailDeps {
  tasksApi?: TaskApiShape;
  windowOpen?: (url: string, target?: string) => void;
  connectEvents?: ConnectEvents;
}

let currentTask: TaskDetail | null = null;
let currentDeps: Required<TaskDetailDeps> = {
  tasksApi: defaultTasksApi,
  windowOpen: (url, target) => {
    window.open(url, target);
  },
  connectEvents: defaultConnectEvents,
};
let dirty = false;
let taskDetailLanguageEvents: AbortController | null = null;

// 历史报告重生成按钮：完整重跑报告生成（LLM 重写 + HTML 渲染）并覆盖旧报告。
// History report-regeneration button: full re-run (LLM rewrite + HTML render), overwrites the old report.
const SHOW_HISTORY_REGENERATE_REPORT_BUTTON = true;

/**
 * 打开可编辑 Task 详情 / Open an editable Task detail modal.
 * 点击后立即打开空浮层 + Spinner，数据返回后再填充；保证 UI 无"卡一下"的阻塞感。
 * Paints an empty modal with a spinner in the same tick as the click,
 * then swaps in the task form / footer once the detail fetch resolves.
 */
export async function openTaskDetail(
  taskId: string,
  deps: TaskDetailDeps = {},
): Promise<TaskDetail | null> {
  const resolvedDeps = resolveDeps(deps);
  const loadingOpened = openDetailModal({
    title: "Task Detail",
    titleMeta: taskUuidMeta(taskId),
    dirty: false,
    render: () => createLoadingBlock("Loading task…"),
  });
  if (!loadingOpened) return null;
  currentDeps = resolvedDeps;
  currentTask = null;
  dirty = false;
  setDetailDirty(false);

  try {
    const body = await resolvedDeps.tasksApi.get!(taskId);
    if (!isDetailModalOpen()) return null;
    fillTaskDetail(body.task);
    currentTask = body.task;
    upsertTask(body.task);
    return body.task;
  } catch (error) {
    showTaskLoadError(error);
    throw error;
  }
}

/**
 * 新建持久化 Task 并打开详情 / Create a persisted Task and open its detail modal.
 * 点击后立即打开空浮层 + Spinner，create + get 完成后再填充真实表单。
 * Opens an empty modal immediately; fills in the form once create + get settle.
 */
export async function createAndOpenTaskDetail(
  body: TaskCreateRequest,
  deps: TaskDetailDeps = {},
): Promise<TaskDetail | null> {
  if (!closeDetailModal()) return null;
  const resolvedDeps = resolveDeps(deps);
  const loadingOpened = openDetailModal({
    title: "Task Detail",
    dirty: false,
    render: () => createLoadingBlock("Creating task…"),
  });
  if (!loadingOpened) return null;
  currentDeps = resolvedDeps;
  currentTask = null;
  dirty = false;
  setDetailDirty(false);

  try {
    const created = await resolvedDeps.tasksApi.create!(body);
    upsertTask(created.task);
    state.workbenchView = "tasks";
    state.selectedTaskId = created.task.id;
    renderCardList();
    const detail = resolvedDeps.tasksApi.get
      ? await resolvedDeps.tasksApi.get(created.task.id)
      : created;
    if (!isDetailModalOpen()) return null;
    fillTaskDetail(detail.task);
    currentTask = detail.task;
    return detail.task;
  } catch (error) {
    showTaskLoadError(error);
    throw error;
  }
}

// 填充 Task 详情表单到已打开的空浮层 / Fill the task form into the already-open loading modal.
function fillTaskDetail(task: TaskDetail): void {
  setDetailModalTitleMeta(taskUuidMeta(task.id));
  replaceDetailModalBody(createTaskForm(task));
  replaceDetailModalFooter(createTaskFooter(task));
  dirty = false;
  setDetailDirty(false);
}

// 标题右侧展示 Task UUID / Show Task UUID beside the modal title.
function taskUuidMeta(taskId: string): string {
  return `UUID: ${taskId}`;
}

// 把加载错误写到详情浮层 / Render a load error into the open modal body.
function showTaskLoadError(error: unknown): void {
  if (!isDetailModalOpen()) return;
  const block = document.createElement("div");
  block.className = "content-loading-error";
  const message = document.createElement("p");
  message.className = "task-detail-error";
  message.textContent = error instanceof Error ? error.message : String(error);
  block.append(message);
  replaceDetailModalBody(block);
  replaceDetailModalFooter(null);
}

/**
 * 启动一个已保存的 Task / Start simulation from a saved Task.
 */
export async function startTaskSimulation(
  taskId: string,
  deps: TaskDetailDeps = {},
): Promise<TaskSimulationStartResponse | null> {
  const d = resolveDeps(deps);
  if (isReportGenerationBusy()) {
    await showReportGenerationBusyModal();
    return null;
  }
  // 启动前先弹确认浮层：单方案扣 1 Credit / A/B 扣 2 Credit。
  // 余额不足时浮层自行切换为红色警示，用户点"知道了"视作取消。
  // Confirm before launching: 1 credit for single mode, 2 for A/B.
  // When balance is insufficient the same modal renders the warning form.
  const task = findTask(taskId) ?? (currentTask?.id === taskId ? currentTask : null);
  const mode = task?.mode ?? state.mode;
  const cost = mode === "ab" ? 2 : 1;
  const currentCredit = state.auth?.user?.credit ?? 0;
  const confirmed = await showConfirmSimulationModal({ cost, currentCredit, mode });
  if (!confirmed) return null;
  // 启动前预检互斥工作，避免发一次一定会 409 的 start 请求。
  // Preflight blocking work to avoid a start request that is certain to 409.
  if (await hasBlockingWorkElsewhere(taskId)) return null;
  try {
    const started = await d.tasksApi.startSimulation!(taskId);
    // 通知终端绑定器切换到新仿真 / Notify the terminal binder about the new simulation.
    // 成功后自动切到 Plan A tab；失败由 binder 自行记录日志，UI 路径静默。
    // On success, switch to Plan A; failures are already logged by the binder.
    void onSimulationStarted(started.simulation_id, mode)
      .then(() => setActiveTab("planA"))
      .catch(() => { /* binder already logs; fail silently in UI path */ });
    state.running = true;
    state.currentTask = {
      kind: "history",
      simulationId: started.simulation_id,
      reportId: null,
      reportStatus: "pending",
    };
    state.lastReportId = null;
    state.timeline = [];
    state.artifacts = {};
    if (task) {
      const queuedTask = mergeQueuedSimulation(task, started);
      upsertTask(queuedTask);
      if (currentTask?.id === taskId) {
        currentTask = mergeQueuedDetail(currentTask, queuedTask.latest_simulation!);
        refreshOpenTaskDetail();
      }
      renderCardList();
      window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
    }
    d.connectEvents(started.simulation_id, (payload) => {
      applyTaskTerminal(taskId, started.simulation_id, payload);
    });
    return started;
  } catch (error) {
    if (handleStartError(error)) return null;
    throw error;
  }
}

// 应用任务模拟终态 / Apply terminal simulation state to the task card.
function applyTaskTerminal(
  taskId: string,
  simulationId: string,
  payload: TerminalPayload,
): void {
  state.running = false;
  if (state.reportGeneration?.simulationId === simulationId) {
    state.reportGeneration = null;
  }
  const task = findTask(taskId);
  const historyItem = task?.latest_simulation?.simulation_id === simulationId
    ? task.latest_simulation
    : currentTask?.id === taskId
      ? currentTask.simulation_history.find((item) => item.simulation_id === simulationId) ?? null
      : null;
  if (!task || !historyItem) return;
  const reportId = payload.report_id ?? payload.simulation?.report_id ?? null;
  const status = resolveTerminalSimulationStatus(payload, historyItem.status);
  const reportStatus = resolveTerminalReportStatus(
    reportId,
    payload.report_status ?? payload.simulation?.report_status ?? null,
    status,
  );
  const latest: TaskHistoryItem = {
    ...historyItem,
    status,
    report_status: reportStatus,
    report_id: reportStatus === "ready" ? reportId : null,
    report_error: payload.error ?? payload.simulation?.report_error ?? null,
    started_at: payload.simulation?.started_at ?? historyItem.started_at,
    completed_at: payload.simulation?.completed_at ?? new Date().toISOString(),
  };
  const isLatestSimulation = task.latest_simulation?.simulation_id === simulationId;
  const newlyReady = reportStatus === "ready" && historyItem.report_status !== "ready";
  const updatedTask: TaskListItem = {
    ...task,
    ready_report_count: newlyReady ? Math.max(task.ready_report_count, 0) + 1 : task.ready_report_count,
    latest_simulation: isLatestSimulation ? latest : task.latest_simulation,
  };
  upsertTask(updatedTask);
  if (state.currentTask?.kind === "history" && state.currentTask.simulationId === simulationId) {
    state.currentTask = {
      kind: "history",
      simulationId,
      reportId: reportStatus === "ready" ? reportId : null,
      reportStatus,
    };
    state.lastReportId = reportStatus === "ready" ? reportId : null;
  }
  if (currentTask?.id === taskId) {
    currentTask = {
      ...currentTask,
      ...updatedTask,
      latest_simulation: isLatestSimulation ? latest : currentTask.latest_simulation,
      simulation_history: replaceHistoryItem(currentTask.simulation_history, latest),
    };
    refreshOpenTaskDetail();
  }
  renderCardList();
  window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
}

/**
 * 获取并打开 Task 报告 / Fetch and open reports for a Task.
 */
export async function openTaskReports(
  taskId: string,
  deps: TaskDetailDeps = {},
): Promise<void> {
  const d = resolveDeps(deps);
  const body = await d.tasksApi.reports!(taskId);
  openReportForSelection(body.reports.map(toPickerReport), d.windowOpen);
}

// 渲染详情浮层 / Render the editable task modal.
function renderTaskDetail(task: TaskDetail): boolean {
  const opened = openDetailModal({
    title: "Task Detail",
    titleMeta: taskUuidMeta(task.id),
    dirty: false,
    render: () => createTaskForm(task),
    footer: () => createTaskFooter(task),
  });
  if (!opened) return false;
  dirty = false;
  setDetailDirty(false);
  bindTaskDetailLanguageRefresh();
  return true;
}

// 创建任务表单 / Create the task edit form.
function createTaskForm(task: TaskDetail): HTMLElement {
  const form = document.createElement("form");
  form.className = "task-detail-form";
  form.addEventListener("submit", (event) => event.preventDefault());

  // 以当前 channel 预先过滤 platform / vertical 选项，保证联动效果从首屏就生效。
  // Pre-filter platform / vertical options with the current channel so the
  // coupling is correct on first render, not only after an interaction.
  const channelRaw = String(task.config["channel"] ?? "");
  const platformOptions = platformOptionsForChannel(channelRaw);
  const verticalOptions = verticalOptionsForChannel(channelRaw);

  form.innerHTML = `
    <label class="task-detail-field">
      <span>Title</span>
      <input class="task-detail-title" name="title" type="text" value="${escapeHtml(task.title)}">
    </label>
    <div class="task-detail-config">
      ${selectField("channel", "Channel", task.config["channel"])}
      ${selectField("platform", "Platform", task.config["platform"], platformOptions)}
      ${selectField("vertical", "Vertical", task.config["vertical"], verticalOptions)}
      ${numberField("max_waves", "Max Waves", task.config["max_waves"])}
      ${textField("simulation_horizon", "Horizon", task.config["simulation_horizon"])}
      ${numberField("ensemble_runs", "Ensemble Runs", task.config["ensemble_runs"])}
      ${numberField("deliberation_rounds", "Deliberation", task.config["deliberation_rounds"])}
    </div>
    ${renderPlanEditor("A", "Plan A", task.plan_a_md, "plan-a.md")}
    ${task.mode === "ab" ? renderPlanEditor("B", "Plan B", task.plan_b_md ?? "", "plan-b.md") : ""}
    <section class="task-detail-history">
      <h3>Simulation History</h3>
      <div class="task-detail-history-list">
        ${renderHistory(task)}
      </div>
    </section>
    <p class="task-detail-error" hidden></p>
  `;

  form.querySelectorAll("input, textarea, select").forEach((node) => {
    node.addEventListener("input", markDirty);
    node.addEventListener("change", markDirty);
  });
  form.querySelector<HTMLInputElement>(".task-detail-upload-a")
    ?.addEventListener("change", (event) => {
      void loadUploadIntoTextarea(event, ".task-detail-plan-a");
    });
  form.querySelector<HTMLInputElement>(".task-detail-upload-b")
    ?.addEventListener("change", (event) => {
      void loadUploadIntoTextarea(event, ".task-detail-plan-b");
    });
  form.querySelector<HTMLButtonElement>(".task-detail-template-a")
    ?.addEventListener("click", () => downloadTemplate(task.mode, "A"));
  form.querySelector<HTMLButtonElement>(".task-detail-template-b")
    ?.addEventListener("click", () => downloadTemplate(task.mode, "B"));
  enhanceTaskDetailSelects(form);
  wireChannelCoupling(form);
  wirePlanEditorTabs(form);
  bindHistoryActions(form, task);
  return form;
}

// Channel × Platform × Vertical 联动 / Couple channel → platform / vertical selects.
// 监听 channel 原生 select 的 change；新渠道与当前 platform/vertical 不兼容时，
// 重建对应的 <option> 列表并 fallback 到合法默认值，同时刷新 custom select 外壳。
// Listen on the channel <select>. When the new channel is incompatible with
// the currently-selected platform / vertical, rebuild the native <option>
// list, fall back to a compatible default, and refresh the custom overlay.
function wireChannelCoupling(form: HTMLElement): void {
  const channelSelect = form.querySelector<HTMLSelectElement>(".task-detail-channel");
  const platformSelect = form.querySelector<HTMLSelectElement>(".task-detail-platform");
  const verticalSelect = form.querySelector<HTMLSelectElement>(".task-detail-vertical");
  if (!channelSelect) return;

  channelSelect.addEventListener("change", () => {
    const channel = channelSelect.value;
    if (platformSelect) {
      syncCoupledSelect(platformSelect, "platform", channel);
    }
    if (verticalSelect) {
      syncCoupledSelect(verticalSelect, "vertical", channel);
    }
  });
}

// 同步一个受 channel 约束的 select / Sync a channel-constrained select.
// 1) 若当前值与新渠道兼容，只重建 option pool（无需触发 change）。
// 2) 否则重建 + 赋默认值 + 派发 change（通知 dirty 与 custom trigger 刷新）。
// When the existing value is still compatible, rebuild the option pool silently.
// When it is not, rebuild, fall back to a default, and dispatch change so the
// dirty tracker and custom trigger update.
function syncCoupledSelect(
  select: HTMLSelectElement,
  kind: "platform" | "vertical",
  channel: string,
): void {
  const options = kind === "platform"
    ? platformOptionsForChannel(channel)
    : verticalOptionsForChannel(channel);
  const currentValue = select.value;
  const compatible = kind === "platform"
    ? isPlatformCompatible(channel, currentValue)
    : isVerticalCompatible(channel, currentValue);
  const nextValue = compatible
    ? currentValue
    : (kind === "platform" ? defaultPlatformForChannel(channel) : defaultVerticalForChannel(channel));

  select.innerHTML = buildFieldOptionsHtml(kind, nextValue, options);
  const changed = currentValue !== nextValue;
  if (changed) {
    select.value = nextValue;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  } else {
    // 即使 value 未变也要让 custom trigger 重建 option 列表与菜单。
    // Fire a change event to rebuild the custom overlay without touching dirty state.
    // 发自定义事件而非原生 change，避免误触发 markDirty。
    select.dispatchEvent(new CustomEvent("ripple:options-rebuilt", { bubbles: true }));
  }
}

// 创建底部操作 / Create modal footer actions.
// 左侧：Save（黄色）+ Close；右侧：Start Simulation（黄色）+ Open Report（次级）。
// 所有 Simulation / Report 按钮与工作台工具栏保持完全一致的 run-command 视觉。
// Layout: Save (amber) + Close on the left; Start (amber) + Open Report (secondary) on the right.
// Run-command buttons reuse the toolbar styling so the detail modal mirrors it exactly.
function createTaskFooter(task: TaskDetail): HTMLElement {
  const actions = document.createElement("div");
  actions.className = "task-detail-actions detail-modal-actions";

  const left = document.createElement("div");
  left.className = "detail-modal-actions-left";

  const save = buildRunCommandButton({
    className: "task-detail-save",
    label: "Save",
    iconKind: "save",
  });
  save.addEventListener("click", () => {
    void saveCurrentTask(save);
  });

  const deleteButton = buildRunCommandButton({
    className: "task-detail-delete",
    label: "Delete",
    iconKind: "delete",
    danger: true,
  });
  deleteButton.addEventListener("click", () => {
    void deleteCurrentTask(deleteButton);
  });

  const close = buildRunCommandButton({
    className: "task-detail-close",
    label: "Close",
    iconKind: "close",
    secondary: true,
  });
  close.addEventListener("click", () => {
    closeDetailModal();
  });

  left.append(save, deleteButton, close);

  const right = document.createElement("div");
  right.className = "detail-modal-actions-right";

  const start = buildRunCommandButton({
    className: "task-detail-start",
    label: "Start Simulation",
    iconKind: "start",
  });
  start.disabled = isReportGenerationBusy() || state.running;
  start.addEventListener("click", () => {
    if (!currentTask || dirty) return;
    const restore = startButtonLoading(start, "Starting");
    void startTaskSimulation(currentTask.id, currentDeps)
      .catch((error) => showTaskError(formatError(error)))
      .finally(restore);
  });

  const report = buildRunCommandButton({
    className: "task-detail-open-report",
    label: "Open Report",
    iconKind: "report",
    secondary: true,
  });
  report.disabled = task.ready_report_count <= 0;
  report.addEventListener("click", () => {
    if (!currentTask) return;
    const restore = startButtonLoading(report, "Opening");
    void openTaskReports(currentTask.id, currentDeps)
      .catch((error) => showTaskError(formatError(error)))
      .finally(restore);
  });

  right.append(start, report);
  actions.append(left, right);
  return actions;
}

// 构造工作台风格的 run-command 按钮 / Build a toolbar-style run-command button.
// 与 index.html 里的 Start / Open Report 保持 DOM 结构一致，便于复用 CSS。
// Keeps DOM structure identical to the toolbar markup so styling lines up.
interface RunCommandButtonOptions {
  className: string;
  label: string;
  iconKind: "start" | "report" | "save" | "delete" | "close";
  secondary?: boolean;
  danger?: boolean;
}

function buildRunCommandButton(opts: RunCommandButtonOptions): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `run-command-button ${opts.secondary ? "secondary " : ""}${opts.danger ? "danger " : ""}${opts.className}`.trim();

  const icon = document.createElement("span");
  // "start" 复用工具栏的默认三角形（无修饰类），其余 kind 作为修饰类。
  // "start" reuses the toolbar's default triangle (no modifier); other kinds add a modifier.
  icon.className = opts.iconKind === "start"
    ? "run-command-icon"
    : `run-command-icon ${opts.iconKind}`;
  icon.setAttribute("aria-hidden", "true");
  if (opts.iconKind === "delete") {
    icon.innerHTML = trashIconSvg();
  }

  const text = document.createElement("span");
  text.textContent = opts.label;

  button.append(icon, text);
  return button;
}

// 统一垃圾桶图标 / Shared trash icon.
function trashIconSvg(): string {
  return `<svg class="trash-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M7 6l1 14h8l1-14"/><path d="M10 11v5"/><path d="M14 11v5"/></svg>`;
}

// 锁定 Task Detail 浮窗按钮 / Lock Task Detail modal buttons.
function startTaskDetailButtonLock(primaryButton: HTMLButtonElement, label: string): () => void {
  const root = document.getElementById("detail-modal-root");
  const buttons = Array.from(root?.querySelectorAll<HTMLButtonElement>("button") ?? []);
  const originalStates = buttons.map((button) => ({
    button,
    disabled: button.disabled,
  }));
  const restorePrimary = startButtonLoading(primaryButton, label);

  for (const button of buttons) {
    if (button !== primaryButton) {
      button.disabled = true;
    }
  }

  return () => {
    restorePrimary();
    for (const { button, disabled } of originalStates) {
      if (button !== primaryButton) {
        button.disabled = disabled;
      }
    }
  };
}

// 删除当前任务 / Delete the current task.
async function deleteCurrentTask(button: HTMLButtonElement): Promise<void> {
  if (!currentTask || !currentDeps.tasksApi.delete) return;
  if (!window.confirm("Delete this task?")) return;
  const taskId = currentTask.id;
  const mode = currentTask.mode;
  const restore = startTaskDetailButtonLock(button, "Deleting");
  try {
    await currentDeps.tasksApi.delete(taskId);
    let nextTasks = state.tasks.filter((task) => task.id !== taskId);
    if (currentDeps.tasksApi.list) {
      try {
        const response = await currentDeps.tasksApi.list(mode);
        nextTasks = response.tasks;
      } catch {
        // 删除已成功时，列表刷新失败也要关闭浮层并用本地状态刷新卡片。
        // If delete succeeded but refresh failed, still close and repaint from local state.
      }
    }
    state.tasks = nextTasks;
    state.selectedTaskId = nextTasks.find((task) => task.mode === mode)?.id ?? null;
    currentTask = null;
    dirty = false;
    setDetailDirty(false);
    closeDetailModal();
    renderCardList();
    window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
  } catch (error) {
    showTaskError(formatError(error));
  } finally {
    restore();
  }
}

// 保存当前任务 / Save the current task.
async function saveCurrentTask(button: HTMLButtonElement): Promise<void> {
  if (!currentTask) return;
  const restore = startButtonLoading(button, "Saving");
  try {
    const payload = readFormPayload(currentTask);
    const response = await currentDeps.tasksApi.update!(currentTask.id, payload);
    currentTask = response.task;
    dirty = false;
    setDetailDirty(false);
    upsertTask(response.task);
    replaceOpenTaskDetail(response.task);
    renderCardList();
    window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
  } catch (error) {
    showTaskError(formatError(error));
  } finally {
    restore();
  }
}

// 标记详情为 dirty / Mark the task detail as dirty.
function markDirty(): void {
  dirty = true;
  setDetailDirty(true);
  const start = document.querySelector<HTMLButtonElement>(".task-detail-start");
  if (start) start.disabled = true;
}

// 读取表单为 PATCH payload / Read form values into the PATCH payload.
function readFormPayload(task: TaskDetail): {
  title: string;
  plan_a_md: string;
  plan_b_md: string | null;
  config: SimulationConfig;
} {
  return {
    title: inputValue(".task-detail-title"),
    plan_a_md: textareaValue(".task-detail-plan-a"),
    plan_b_md: task.mode === "ab" ? textareaValue(".task-detail-plan-b") : null,
    config: {
      channel: selectValue(".task-detail-channel"),
      platform: selectValue(".task-detail-platform"),
      vertical: selectValue(".task-detail-vertical"),
      max_waves: numberValue(".task-detail-max-waves", 24),
      simulation_horizon: inputValue(".task-detail-simulation-horizon") || "72h",
      ensemble_runs: numberValue(".task-detail-ensemble-runs", 1),
      deliberation_rounds: numberValue(".task-detail-deliberation-rounds", 3),
    },
  };
}

// 上传文件到 textarea / Load an uploaded Markdown file into a textarea.
async function loadUploadIntoTextarea(event: Event, selector: string): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const textarea = document.querySelector<HTMLTextAreaElement>(selector);
  if (!textarea) return;
  textarea.value = await readFileText(file);
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

// 读取文件文本，兼容缺少 file.text() 的测试运行时 / Read file text, including runtimes without file.text().
function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result ?? "")));
    reader.addEventListener("error", () => reject(reader.error ?? new Error("file read failed")));
    reader.readAsText(file);
  });
}

// 合并刚启动的模拟 / Merge queued simulation into a task summary.
function mergeQueuedSimulation(
  task: TaskListItem,
  started: TaskSimulationStartResponse,
): TaskListItem {
  const now = new Date().toISOString();
  return {
    ...task,
    simulation_count: task.simulation_count + 1,
    latest_simulation: {
      simulation_id: started.simulation_id,
      status: started.status,
      report_status: "pending",
      report_id: null,
      created_at: now,
      started_at: null,
      completed_at: null,
    },
  };
}

// 合并排队态到当前详情历史 / Merge queued state into the open detail history.
function mergeQueuedDetail(task: TaskDetail, latest: TaskHistoryItem): TaskDetail {
  return {
    ...task,
    simulation_count: Math.max(task.simulation_count, 0) + 1,
    latest_simulation: latest,
    simulation_history: [latest, ...task.simulation_history],
  };
}

// 替换历史列表中的单条模拟 / Replace one simulation in the detail history.
function replaceHistoryItem(history: TaskHistoryItem[], latest: TaskHistoryItem): TaskHistoryItem[] {
  let found = false;
  const next = history.map((item) => {
    if (item.simulation_id !== latest.simulation_id) return item;
    found = true;
    return latest;
  });
  return found ? next : [latest, ...next];
}

// 本地标记报告生成中 / Locally mark a history row as regenerating.
function patchHistoryReportPending(task: TaskDetail, simulationId: string): TaskDetail {
  const patch = (item: TaskHistoryItem): TaskHistoryItem =>
    item.simulation_id === simulationId
      ? { ...item, report_status: "pending", report_error: null }
      : item;
  return {
    ...task,
    latest_simulation: task.latest_simulation ? patch(task.latest_simulation) : null,
    simulation_history: task.simulation_history.map(patch),
  };
}

// 刷新当前打开的 Task 详情 / Refresh the open Task detail modal.
function refreshOpenTaskDetail(): void {
  const root = document.getElementById("detail-modal-root");
  if (!currentTask || dirty || !root || root.hidden || !root.querySelector(".task-detail-form")) {
    return;
  }
  replaceOpenTaskDetail(currentTask);
}

// 刷新已打开 Task 浮层的动态语言标签 / Refresh dynamic labels in an open Task modal.
function bindTaskDetailLanguageRefresh(): void {
  taskDetailLanguageEvents?.abort();
  taskDetailLanguageEvents = new AbortController();
  window.addEventListener("ripple:language-changed", refreshOpenTaskConfigLabels, {
    signal: taskDetailLanguageEvents.signal,
  });
}

// 只更新 Channel / Platform / Vertical option 文案，保留用户编辑中的内容。
// Only relabel Channel / Platform / Vertical options; preserve in-progress edits.
function refreshOpenTaskConfigLabels(): void {
  const root = document.getElementById("detail-modal-root");
  const form = root?.querySelector<HTMLElement>(".task-detail-form");
  if (!root || root.hidden || !form) return;
  const channelSelect = form.querySelector<HTMLSelectElement>(".task-detail-channel");
  const channel = channelSelect?.value ?? "";
  for (const key of ["channel", "platform", "vertical"] as const satisfies readonly ConfigKind[]) {
    const select = form.querySelector<HTMLSelectElement>(`.task-detail-${kebab(key)}`);
    if (!select) continue;
    const selectedValue = select.value;
    const baseOptions = key === "platform"
      ? platformOptionsForChannel(channel)
      : key === "vertical"
        ? verticalOptionsForChannel(channel)
        : getConfigOptions(key);
    select.innerHTML = buildFieldOptionsHtml(key, selectedValue, baseOptions);
    select.value = selectedValue;
    select.dispatchEvent(new CustomEvent("ripple:options-rebuilt", { bubbles: true }));
  }
}

// 替换详情内容但不重建浮层 / Replace detail content without recreating the modal.
function replaceOpenTaskDetail(task: TaskDetail): void {
  const root = document.getElementById("detail-modal-root");
  const body = root?.querySelector<HTMLElement>(".detail-modal-body");
  const footer = root?.querySelector<HTMLElement>(".detail-modal-footer");
  if (!root || root.hidden || !body) {
    renderTaskDetail(task);
    return;
  }
  body.replaceChildren(createTaskForm(task));
  footer?.replaceChildren(createTaskFooter(task));
  dirty = false;
  setDetailDirty(false);
}

// 写入任务卡片状态 / Upsert task into card state.
function upsertTask(task: TaskListItem): void {
  state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
  state.selectedTaskId = task.id;
}

// 查找任务卡片 / Find task card.
function findTask(taskId: string): TaskListItem | null {
  return state.tasks.find((task) => task.id === taskId) ?? null;
}

// 启动前检测是否有互斥工作 / Preflight: is there blocking simulation or report work?
// running sim 属于同一 task 的既有状态时不拦截（理论上 UI 已禁用 start 按钮，这里只是兜底）。
// If another run or report generation is active, show an info modal and return true
// so the caller knows to bail out without firing the start request.
async function hasBlockingWorkElsewhere(taskId: string): Promise<boolean> {
  try {
    const body = await simulationsApi.active();
    const active = body.simulation;
    if (!active) {
      if (body.latest?.status === "completed" && body.latest.report_status === "pending") {
        state.reportGeneration = {
          simulationId: body.latest.id,
          taskId: body.latest.task_id ?? null,
          jobId: null,
          status: "pending",
        };
        await showReportGenerationBusyModal();
        window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
        return true;
      }
      return false;
    }
    const ownerTaskId = (active as { task_id?: string }).task_id;
    if (ownerTaskId && ownerTaskId === taskId) return false;
    await showInfoModal({
      title: "Simulation already running",
      message: "Another simulation is already running. Please wait for it to finish before starting a new one.",
      tone: "info",
    });
    return true;
  } catch {
    // 预检失败不阻塞，继续让 startSimulation 走一遍再由错误处理回应。
    // If the preflight itself fails, fall through to the normal start path.
    return false;
  }
}

// 报告生成忙碌提示 / Report-generation busy modal.
async function showReportGenerationBusyModal(): Promise<void> {
  await showInfoModal({
    title: "Report generation in progress",
    message: "A report is currently being generated. Please wait for it to finish before starting a new simulation.",
    tone: "info",
  });
}

// 报告生成全局忙碌态 / Global report-generation busy state.
function isReportGenerationBusy(): boolean {
  return Boolean(state.reportGeneration) || currentBinding().kind === "bound_report";
}

// 后端启动错误处理 / Handle backend start errors.
// 普通提示类错误走 info modal，避免把红色错误条喷到卡片区或 Task 浮层。
// Informational errors get surfaced via the info modal rather than a red banner.
function handleStartError(error: unknown): boolean {
  if (error instanceof InsufficientCreditError) {
    showInsufficientCreditModal(error.cost, error.current);
    return true;
  }
  if (error instanceof ActiveSimulationError) {
    void showInfoModal({
      title: "Simulation already running",
      message: "Another simulation is already running. Please wait for it to finish before starting a new one.",
      tone: "info",
    });
    return true;
  }
  if (error instanceof ActiveReportGenerationError) {
    void showInfoModal({
      title: "Report generation in progress",
      message: "A report is currently being generated. Please wait for it to finish before starting a new simulation.",
      tone: "info",
    });
    return true;
  }
  if (error instanceof RateLimitedError) {
    void showInfoModal({
      title: "Too many requests",
      message: error.message || "Please wait a moment and try again.",
      tone: "warn",
    });
    return true;
  }
  return false;
}

// 报告选择项转换 / Convert task reports to report-picker items.
function toPickerReport(report: TaskReportSummary): {
  report_id: string;
  title: string;
  viewer_url: string;
  created_at: string;
} {
  return {
    report_id: report.report_id,
    title: report.title,
    viewer_url: report.viewer_url,
    created_at: report.created_at,
  };
}

// 渲染历史 / Render simulation history.
function renderHistory(task: TaskDetail): string {
  if (!task.simulation_history.length) return `<p class="task-detail-empty">No simulations yet.</p>`;
  return task.simulation_history
    .map((item) => {
      const hasReport = Boolean(item.report_id);
      const reportPending = item.report_status === "pending";
      const active = isActiveHistorySimulation(item);
      const canRegenerateReport = item.status === "completed";
      const reportBusy = isReportGenerationBusy();
      const regenerateDisabled = reportPending || reportBusy;
      const graphDisabled = item.status !== "completed";
      const regenerateVisibilityAttrs = SHOW_HISTORY_REGENERATE_REPORT_BUTTON
        ? ""
        : `hidden aria-hidden="true" tabindex="-1"`;
      return `
      <div class="task-detail-history-item" data-simulation-id="${escapeHtml(item.simulation_id)}">
        <time class="task-detail-history-date" datetime="${escapeHtml(item.created_at)}">${escapeHtml(formatHistoryDate(item.created_at))}</time>
        <span class="task-detail-history-status ${escapeHtml(historyStatusTone(item))}">
          <span class="task-detail-history-status-icon" aria-hidden="true"></span>
          <span>${escapeHtml(historyStatusLabel(item))}</span>
        </span>
        <span class="task-detail-history-actions">
          ${hasReport ? `
            <button
              class="task-detail-history-icon-button task-detail-history-report"
              type="button"
              aria-label="${reportPending ? "Open previous report" : "Open report"}"
              title="${reportPending ? "Open previous report" : "Open report"}"
              data-report-id="${escapeHtml(item.report_id!)}"
            ></button>
          ` : ""}
          ${canRegenerateReport ? `
            <button
              class="task-detail-history-icon-button task-detail-history-regenerate${reportPending ? " is-loading" : ""}"
              type="button"
              aria-label="Regenerate report"
              title="${reportPending ? "Generating report" : reportBusy ? "Another report is generating" : "Regenerate report"}"
              ${regenerateDisabled ? "disabled" : ""}
              ${reportPending ? `aria-busy="true"` : ""}
              ${regenerateVisibilityAttrs}
            >${reportPending ? `<span class="button-spinner" aria-hidden="true"></span>` : ""}</button>
          ` : ""}
          ${item.status === "completed" ? `
            <button
              class="task-detail-history-icon-button task-detail-history-view-graph"
              type="button"
              aria-label="View graph"
              title="View graph"
            ></button>
          ` : ""}
          <button
            class="task-detail-history-icon-button task-detail-history-graph"
            type="button"
            aria-label="Regenerate graph"
            title="${graphDisabled ? "Graph can be regenerated after simulation completes" : "Regenerate graph"}"
            ${graphDisabled ? "disabled" : ""}
          ></button>
          <button
            class="task-detail-history-icon-button task-detail-history-info"
            type="button"
            aria-label="Simulation details"
            title="Simulation details"
          ></button>
          ${active ? `
            <button
              class="task-detail-history-icon-button task-detail-history-stop"
              type="button"
              aria-label="Stop simulation"
              title="Stop simulation"
            ></button>
          ` : `
            <button
              class="task-detail-history-icon-button task-detail-history-delete"
              type="button"
              aria-label="Delete simulation"
              title="Delete simulation"
            >${trashIconSvg()}</button>
          `}
        </span>
      </div>
    `;
    })
    .join("");
}

// 历史行活跃模拟 / Active simulation rows in history.
function isActiveHistorySimulation(item: TaskHistoryItem): boolean {
  return item.status === "queued" || item.status === "running";
}

// 绑定历史行按钮 / Wire history row actions.
function bindHistoryActions(root: HTMLElement, task: TaskDetail): void {
  const items = new Map(task.simulation_history.map((item) => [item.simulation_id, item]));
  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-report").forEach((button) => {
    button.addEventListener("click", () => {
      const reportId = button.dataset["reportId"];
      if (!reportId) return;
      currentDeps.windowOpen(`/reports/${encodeURIComponent(reportId)}`, "_blank");
    });
  });

  // View graph 直达 /graph/{sim_id} 新标签页 / Deep link to /graph/{sim_id} in a new tab.
  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-view-graph").forEach((button) => {
    button.addEventListener("click", () => {
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      if (!simulationId) return;
      currentDeps.windowOpen(`/graph/${encodeURIComponent(simulationId)}`, "_blank");
    });
  });

  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-info").forEach((button) => {
    button.addEventListener("click", () => {
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      const item = simulationId ? items.get(simulationId) : null;
      if (item) showSimulationHistoryInfo(item);
    });
  });

  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-regenerate").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.disabled || isReportGenerationBusy()) return;
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      if (!simulationId) return;
      if (!window.confirm("Regenerate this report? The new report will overwrite the existing report.")) return;
      const restore = startIconButtonLoading(button);
      void regenerateHistoryReport(task.id, simulationId)
        .catch((error) => showTaskError(formatError(error)))
        .finally(restore);
    });
  });

  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-graph").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.disabled) return;
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      if (!simulationId) return;
      const restore = startIconButtonLoading(button);
      void regenerateHistoryGraph(simulationId)
        .catch((error) => showTaskError(formatError(error)))
        .finally(restore);
    });
  });

  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-delete").forEach((button) => {
    button.addEventListener("click", () => {
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      if (!simulationId) return;
      if (!window.confirm("Delete this simulation history item?")) return;
      const restore = startIconButtonLoading(button);
      void deleteHistorySimulation(task.id, simulationId)
        .catch((error) => showTaskError(formatError(error)))
        .finally(restore);
    });
  });

  root.querySelectorAll<HTMLButtonElement>(".task-detail-history-stop").forEach((button) => {
    button.addEventListener("click", () => {
      const simulationId = button.closest<HTMLElement>(".task-detail-history-item")?.dataset["simulationId"];
      if (!simulationId) return;
      void stopHistorySimulation(simulationId, button);
    });
  });
}

// 图标按钮 loading 态 / Loading state for icon-only buttons.
function startIconButtonLoading(button: HTMLButtonElement): () => void {
  const originalHtml = button.innerHTML;
  const originalDisabled = button.disabled;
  let restored = false;

  button.disabled = true;
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");

  const spinner = document.createElement("span");
  spinner.className = "button-spinner";
  spinner.setAttribute("aria-hidden", "true");
  button.replaceChildren(spinner);

  return () => {
    if (restored) return;
    restored = true;
    button.disabled = originalDisabled;
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    button.innerHTML = originalHtml;
  };
}

// 删除历史模拟并刷新 Task / Delete one history simulation and refresh the Task.
async function deleteHistorySimulation(taskId: string, simulationId: string): Promise<void> {
  await simulationsApi.delete(simulationId);
  const response = await currentDeps.tasksApi.get!(taskId);
  currentTask = response.task;
  upsertTask(response.task);
  replaceOpenTaskDetail(response.task);
  renderCardList();
  window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
}

// 重新生成历史模拟报告 / Regenerate a report for one completed history simulation.
async function regenerateHistoryReport(taskId: string, simulationId: string): Promise<void> {
  const previousReportId = currentTask?.simulation_history.find(
    (item) => item.simulation_id === simulationId,
  )?.report_id ?? null;
  const result = await simulationsApi.regenerateReport(simulationId);
  state.reportGeneration = {
    simulationId,
    taskId,
    jobId: result.report.job_id,
    status: "pending",
  };
  state.currentTask = {
    kind: "history",
    simulationId,
    reportId: null,
    reportStatus: "pending",
  };
  state.lastReportId = null;
  appendLog(`report regeneration queued simulation=${simulationId} job=${result.report.job_id}`);
  const response = await currentDeps.tasksApi.get!(taskId);
  currentTask = patchHistoryReportPending(response.task, simulationId);
  upsertTask(currentTask);
  replaceOpenTaskDetail(currentTask);
  renderCardList();

  void onSimulationStarted(simulationId, response.task.mode)
    .then(() => setActiveTab("planA"))
    .catch(() => { /* binder already logs; fail silently in UI path */ });

  currentDeps.connectEvents(simulationId, (payload) => {
    applyTaskTerminal(taskId, simulationId, payload);
    // SSE 终态到达时广播给其它同源 tab（已打开的报告页 / 其它 task detail）/
    // Broadcast the new report id to any other same-origin tab.
    const nextReportId = payload.report_id ?? payload.simulation?.report_id ?? null;
    if (
      payload.type === "report.regenerated" &&
      nextReportId &&
      previousReportId &&
      nextReportId !== previousReportId
    ) {
      notifyReportUpdated({
        simulationId,
        oldReportId: previousReportId,
        newReportId: nextReportId,
      });
    }
  });
  window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
}

// 重新生成历史模拟 Graph / Regenerate graph data for one completed history simulation.
async function regenerateHistoryGraph(simulationId: string): Promise<void> {
  await simulationsApi.regenerateGraph(simulationId);
  await showInfoModal({
    title: "Graph regenerated",
    message: "Graph data has been rebuilt for this simulation.",
    tone: "info",
  });
}

// 终止历史中的活跃模拟 / Stop an active simulation from history.
async function stopHistorySimulation(simulationId: string, button: HTMLButtonElement): Promise<void> {
  const proceed = await showStopSimulationModal();
  if (!proceed) return;
  const restore = startIconButtonLoading(button);
  try {
    await simulationsApi.cancel(simulationId);
  } catch (error) {
    void showInfoModal({
      title: "Stop failed",
      message: error instanceof Error
        ? `Unable to stop the current simulation: ${error.message}`
        : "Unable to stop the current simulation. Please try again later.",
      tone: "warn",
    });
  } finally {
    restore();
  }
}

// 展示模拟排错信息浮层 / Show simulation troubleshooting details.
function showSimulationHistoryInfo(item: TaskHistoryItem): void {
  let root = document.getElementById("simulation-detail-modal-root");
  if (!root) {
    root = document.createElement("div");
    root.id = "simulation-detail-modal-root";
    document.body.append(root);
  }
  root.hidden = false;
  document.body.classList.add("simulation-detail-modal-open");
  root.innerHTML = `
    <div class="simulation-detail-overlay" aria-hidden="true"></div>
    <section class="simulation-detail-panel" role="dialog" aria-modal="true" aria-labelledby="simulation-detail-title">
      <header class="simulation-detail-header">
        <h3 id="simulation-detail-title">Simulation Details</h3>
        <button class="simulation-detail-close" type="button" aria-label="Close">×</button>
      </header>
      <div class="simulation-detail-body">
        ${simulationDetailRow("Simulation UUID", item.simulation_id)}
        ${simulationDetailRow("Report UUID", item.report_id ?? "No report")}
        ${simulationDetailRow("Created", formatHistoryDate(item.created_at))}
        ${simulationDetailRow("Started", formatHistoryDate(item.started_at))}
        ${simulationDetailRow("Completed", formatHistoryDate(item.completed_at))}
      </div>
    </section>
  `;
  root.querySelector<HTMLButtonElement>(".simulation-detail-close")?.addEventListener("click", () => {
    closeSimulationHistoryInfo(root);
  });
}

// 关闭模拟排错信息浮层 / Close simulation troubleshooting details.
function closeSimulationHistoryInfo(root: HTMLElement): void {
  root.hidden = true;
  root.replaceChildren();
  document.body.classList.remove("simulation-detail-modal-open");
}

// 排错信息行 / Troubleshooting detail row.
function simulationDetailRow(label: string, value: string): string {
  return `
    <div class="simulation-detail-row">
      <span>${escapeHtml(label)}</span>
      <code>${escapeHtml(value)}</code>
    </div>
  `;
}

// 历史状态文案 / History status label.
function historyStatusLabel(item: TaskHistoryItem): string {
  return `${simulationStatusLabel(item.status)} · ${reportStatusLabel(item.report_status)}`;
}

// 历史状态颜色 / History status visual tone.
function historyStatusTone(item: TaskHistoryItem): string {
  if (item.status === "failed" || item.status === "timeout" || item.report_status === "failed") {
    return "is-failed";
  }
  if (item.status === "completed" && item.report_status === "ready") return "is-ready";
  if (item.status === "cancelled") return "is-cancelled";
  return "is-pending";
}

// 模拟状态文案 / Simulation status label.
function simulationStatusLabel(status: SimulationStatus): string {
  const labels: Record<SimulationStatus, string> = {
    queued: "Queued",
    running: "Running",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
    timeout: "Timed Out",
  };
  return labels[status] ?? status;
}

// 报告状态文案 / Report status label.
function reportStatusLabel(status: ReportStatus): string {
  const labels: Record<ReportStatus, string> = {
    pending: "Generating Report",
    ready: "Report Ready",
    failed: "Report Failed",
  };
  return labels[status] ?? status;
}

// 格式化历史时间 / Format history timestamp as yyyy-MM-dd hh:mm:ss.
function formatHistoryDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.replace("T", " ").replace(/\.\d+Z?$/, "");
  const yyyy = String(date.getFullYear());
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

// 两位数字补零 / Pad a number to two digits.
function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

// 渲染 Plan 编辑器（带 Rendered / Source tab）/ Render a Plan editor with Rendered/Source tabs.
// Rendered 模式显示 markdown 预览；Source 模式显示 textarea 可编辑。
// textarea 始终保留在 DOM 中（只靠 CSS 隐藏）以兼容 readFormPayload 与测试选择器。
// Rendered tab shows the markdown preview; Source tab shows the editable textarea.
// The textarea stays in the DOM (hidden via CSS) so readFormPayload + tests still work.
function renderPlanEditor(
  slot: "A" | "B",
  title: string,
  markdown: string,
  filename: string,
): string {
  const slotLower = slot.toLowerCase();
  return `
    <section class="task-detail-plan-editor" data-plan-slot="${slotLower}">
      <div class="task-detail-plan-head">
        <h3>${escapeHtml(title)}</h3>
        <label class="button ghost">
          Upload Markdown
          <input class="task-detail-upload-${slotLower}" type="file" accept=".md,.markdown,text/markdown,text/plain">
        </label>
        <button class="button ghost task-detail-template-${slotLower}" type="button">Download Template</button>
      </div>
      <div class="plan-tab-strip" role="tablist" aria-label="${escapeHtml(title)} view mode">
        <button
          class="plan-tab plan-tab-rendered active"
          type="button"
          role="tab"
          aria-selected="true"
          data-plan-tab="rendered"
        >
          <span class="plan-tab-indicator" aria-hidden="true"></span>
          <span>Rendered</span>
        </button>
        <button
          class="plan-tab plan-tab-source"
          type="button"
          role="tab"
          aria-selected="false"
          data-plan-tab="source"
        >
          <span class="plan-tab-indicator" aria-hidden="true"></span>
          <span>Source</span>
        </button>
      </div>
      <div class="plan-tab-panels">
        <div class="plan-tab-panel plan-tab-panel-rendered active" role="tabpanel">
          <div class="task-detail-preview">${renderPlanMarkdownPreviewHtml(markdown, { slot, filename })}</div>
        </div>
        <div class="plan-tab-panel plan-tab-panel-source" role="tabpanel" hidden>
          <textarea class="task-detail-plan-${slotLower}" rows="14">${escapeHtml(markdown)}</textarea>
        </div>
      </div>
    </section>
  `;
}

// 绑定 Plan 编辑器的 Rendered/Source tab 切换与预览动态渲染。
// Wire the Rendered/Source tab switch and dynamic preview rendering.
function wirePlanEditorTabs(root: HTMLElement): void {
  const editors = Array.from(root.querySelectorAll<HTMLElement>(".task-detail-plan-editor"));
  for (const editor of editors) {
    const slot = (editor.dataset["planSlot"] ?? "a").toUpperCase() as "A" | "B";
    const slotLower = slot.toLowerCase();
    const tabs = Array.from(editor.querySelectorAll<HTMLButtonElement>(".plan-tab"));
    const renderedPanel = editor.querySelector<HTMLElement>(".plan-tab-panel-rendered");
    const sourcePanel = editor.querySelector<HTMLElement>(".plan-tab-panel-source");
    const preview = editor.querySelector<HTMLElement>(".task-detail-preview");
    const textarea = editor.querySelector<HTMLTextAreaElement>(`.task-detail-plan-${slotLower}`);
    if (!renderedPanel || !sourcePanel || !preview || !textarea) continue;

    const filename = slot === "A" ? "plan-a.md" : "plan-b.md";

    const setTab = (mode: "rendered" | "source"): void => {
      const isSource = mode === "source";
      for (const tab of tabs) {
        const active = tab.dataset["planTab"] === mode;
        tab.classList.toggle("active", active);
        tab.setAttribute("aria-selected", String(active));
      }
      renderedPanel.classList.toggle("active", !isSource);
      renderedPanel.hidden = isSource;
      sourcePanel.classList.toggle("active", isSource);
      sourcePanel.hidden = !isSource;
      editor.dataset["planTabMode"] = mode;
      // 切到 Rendered 时用当前 textarea 内容重新渲染，体现编辑结果。
      // Re-render the preview from the current textarea value when switching to Rendered.
      if (!isSource) {
        preview.innerHTML = renderPlanMarkdownPreviewHtml(textarea.value, { slot, filename });
      } else {
        // 切到 Source 时把焦点交给 textarea，方便继续编辑。
        // Focus the textarea so keyboard editing starts right away.
        textarea.focus({ preventScroll: true });
      }
    };

    for (const tab of tabs) {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        const mode = (tab.dataset["planTab"] as "rendered" | "source") ?? "rendered";
        setTab(mode);
      });
    }

    // 初始默认 Rendered / Default to the Rendered tab.
    editor.dataset["planTabMode"] = "rendered";
  }
}

// 选择字段 / Render a select field.
// 渲染原生 <select>（隐藏，保留 form 取值与测试兼容）+ 紧挨的自定义 trigger/menu 容器，
// enhanceTaskDetailSelects() 会在表单挂载后把它升级成契合主题的下拉。
// Renders a hidden native <select> (keeps readFormPayload + test selectors working)
// plus the custom trigger/menu shell next to it. enhanceTaskDetailSelects() upgrades
// the widget once the form is in the DOM.
function selectField(
  key: ConfigKind,
  label: string,
  value: unknown,
  baseOptions?: ConfigOption[],
): string {
  const selectedValue = String(value ?? "");
  const options = buildFieldOptionsHtml(key, selectedValue, baseOptions);
  return `
    <label class="task-detail-field">
      <span>${escapeHtml(label)}</span>
      <div class="task-detail-select" data-config-kind="${escapeHtml(key)}">
        <select class="task-detail-${escapeHtml(kebab(key))} task-detail-native-select">${options}</select>
      </div>
    </label>
  `;
}

// 构造 <option> 列表 HTML / Build option HTML honouring the current value.
// 若 selectedValue 未在 baseOptions 中（如 channel 联动后 legacy 值），
// 自动补一条保留原值的 option，避免覆盖用户已选。
// When selectedValue is not in baseOptions, prepend a preserving option so
// legacy values survive a channel change.
function buildFieldOptionsHtml(
  key: ConfigKind,
  selectedValue: string,
  baseOptions?: ConfigOption[],
): string {
  const pool = baseOptions ?? getConfigOptions(key);
  const known = pool.some((item) => item.value === selectedValue);
  const list = known
    ? pool
    : [
      { value: selectedValue, label: formatConfigLabel(key, selectedValue) },
      ...pool,
    ];
  return list
    .map((item) => `
      <option value="${escapeHtml(item.value)}" ${selectedValue === item.value ? "selected" : ""}>
        ${escapeHtml(item.label)}
      </option>
    `)
    .join("");
}

// 升级 Task 浮层中的原生 select 为自定义下拉 / Upgrade task detail selects to custom dropdowns.
// 视觉上与工作台工具栏 / 浮层底部按钮保持相同的科技感（琥珀色高亮 + 直角边框）。
// Visual style mirrors the toolbar / modal buttons: amber accent + hard edges.
function enhanceTaskDetailSelects(root: HTMLElement): void {
  const wrappers = Array.from(root.querySelectorAll<HTMLElement>(".task-detail-select"));
  for (const wrapper of wrappers) {
    const select = wrapper.querySelector<HTMLSelectElement>("select");
    if (!select) continue;
    // 原生 select 在视觉上隐藏但保留 DOM（form 取值、dirty 事件、测试依赖它）。
    // The native select stays in the DOM but is visually hidden.
    select.classList.add("native-select-hidden");

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "task-select-trigger";
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");

    const menu = document.createElement("div");
    menu.className = "task-select-menu";
    menu.setAttribute("role", "listbox");
    menu.hidden = true;

    wrapper.append(trigger, menu);

    const syncTrigger = (): void => {
      const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex];
      trigger.textContent = selected ? selected.textContent : "—";
      trigger.disabled = select.disabled;
    };

    const renderMenu = (): void => {
      menu.replaceChildren();
      for (const option of Array.from(select.options)) {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "task-select-option";
        item.textContent = option.textContent ?? "";
        item.dataset["value"] = option.value;
        item.setAttribute("role", "option");
        item.setAttribute("aria-selected", String(option.selected));
        item.classList.toggle("active", option.selected);
        item.addEventListener("click", (event) => {
          event.stopPropagation();
          if (select.value !== option.value) {
            select.value = option.value;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
          closeThisMenu();
        });
        menu.append(item);
      }
    };

    const openThisMenu = (): void => {
      closeAllTaskSelectMenus();
      renderMenu();
      menu.hidden = false;
      wrapper.classList.add("open");
      trigger.setAttribute("aria-expanded", "true");
    };

    const closeThisMenu = (): void => {
      menu.hidden = true;
      wrapper.classList.remove("open");
      trigger.setAttribute("aria-expanded", "false");
    };

    trigger.addEventListener("click", (event) => {
      event.stopPropagation();
      if (menu.hidden) {
        openThisMenu();
      } else {
        closeThisMenu();
      }
    });

    // 原生 select 被测试或上传流修改时，自动刷新 trigger 文案。
    // Keep the trigger text in sync when the native select changes (tests + uploads).
    select.addEventListener("change", syncTrigger);
    // Channel 联动重建 option pool 时会派发这个自定义事件，打开菜单时自然渲染最新列表。
    // Coupling rebuilds dispatch this custom event so the trigger mirrors the
    // new option set without a change event.
    select.addEventListener("ripple:options-rebuilt", syncTrigger);

    syncTrigger();
  }
}

// 关闭表单内所有打开的 task select 菜单 / Close every open task select menu.
// 点击浮层内其他区域、下拉框外部时由全局监听触发。
// Called by the global document listener when clicking outside any task select.
function closeAllTaskSelectMenus(): void {
  for (const wrapper of Array.from(document.querySelectorAll<HTMLElement>(".task-detail-select.open"))) {
    const trigger = wrapper.querySelector<HTMLButtonElement>(".task-select-trigger");
    const menu = wrapper.querySelector<HTMLDivElement>(".task-select-menu");
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    if (menu) menu.hidden = true;
    wrapper.classList.remove("open");
  }
}

// 全局 document 监听：点击外部自动收起（遵守 AGENTS.md 前端下拉约定）。
// Document listener: outside click closes the menu (AGENTS.md dropdown rule).
if (typeof document !== "undefined") {
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    if (!target || !target.closest(".task-detail-select")) {
      closeAllTaskSelectMenus();
    }
  });
}

// 数字字段 / Render a number field.
function numberField(key: string, label: string, value: unknown): string {
  return `
    <label class="task-detail-field">
      <span>${escapeHtml(label)}</span>
      <input class="task-detail-${escapeHtml(kebab(key))}" type="number" min="1" value="${escapeHtml(String(value ?? 1))}">
    </label>
  `;
}

// 文本字段 / Render a text field.
function textField(key: string, label: string, value: unknown): string {
  return `
    <label class="task-detail-field">
      <span>${escapeHtml(label)}</span>
      <input class="task-detail-${escapeHtml(kebab(key))}" type="text" value="${escapeHtml(String(value ?? ""))}">
    </label>
  `;
}

// 显示错误 / Show inline task-detail error.
function showTaskError(message: string): void {
  const node = document.querySelector<HTMLElement>(".task-detail-error");
  if (node) {
    node.hidden = false;
    node.textContent = message;
    return;
  }
  showWorkbenchTaskError(message);
}

// 显示工具栏路径错误，不替换卡片列表 / Show toolbar errors without replacing cards.
function showWorkbenchTaskError(message: string): void {
  const root = document.getElementById("card-workspace");
  if (!root) return;
  let node = root.querySelector<HTMLElement>(".task-detail-toolbar-error");
  if (!node) {
    node = document.createElement("p");
    node.className = "workbench-error task-detail-toolbar-error";
    node.setAttribute("role", "alert");
    root.insertBefore(node, root.firstElementChild?.nextSibling ?? null);
  }
  node.textContent = message;
}

function inputValue(selector: string): string {
  return document.querySelector<HTMLInputElement>(selector)?.value.trim() ?? "";
}

function textareaValue(selector: string): string {
  return document.querySelector<HTMLTextAreaElement>(selector)?.value ?? "";
}

function selectValue(selector: string): string {
  return document.querySelector<HTMLSelectElement>(selector)?.value ?? "";
}

function numberValue(selector: string, fallback: number): number {
  const value = Number.parseInt(inputValue(selector), 10);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

// 规范化终态模拟状态 / Normalize terminal simulation status.
function resolveTerminalSimulationStatus(
  payload: TerminalPayload,
  fallback: SimulationStatus,
): SimulationStatus {
  const explicit = payload.simulation?.status ?? payload.status;
  if (isSimulationStatus(explicit)) return explicit;
  switch (payload.type) {
    case "run.completed":
      return "completed";
    case "run.failed":
      return "failed";
    case "run.cancelled":
      return "cancelled";
    case "run.timeout":
      return "timeout";
    default:
      return fallback;
  }
}

// 规范化终态报告状态 / Normalize terminal report status.
function resolveTerminalReportStatus(
  reportId: string | null,
  reportStatus: ReportStatus | null,
  simulationStatus: string | null,
): ReportStatus {
  if (reportId) return "ready";
  if (reportStatus) return reportStatus;
  if (
    simulationStatus === "failed" ||
    simulationStatus === "cancelled" ||
    simulationStatus === "timeout"
  ) {
    return "failed";
  }
  return "pending";
}

// 是否为已知模拟状态 / Whether a value is a known simulation status.
function isSimulationStatus(value: unknown): value is SimulationStatus {
  return (
    value === "queued" ||
    value === "running" ||
    value === "completed" ||
    value === "failed" ||
    value === "cancelled" ||
    value === "timeout"
  );
}

function kebab(value: string): string {
  return value.replace(/_/g, "-");
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function resolveDeps(deps: TaskDetailDeps): Required<TaskDetailDeps> {
  return {
    tasksApi: { ...defaultTasksApi, ...(deps.tasksApi ?? {}) },
    windowOpen: deps.windowOpen ?? ((url, target) => window.open(url, target)),
    connectEvents: deps.connectEvents ?? defaultConnectEvents,
  };
}
